import styled from '@emotion/styled';
import { formatters } from 'folio-common-utils';
import * as React from 'react';
import { lazyWithPreload } from 'react-lazy-with-preload';
import { type RootState, useActions, useSelector } from '../../state';
import type { CoreInfo, Person } from '../../state/editor';
import {
  type Owner,
  getBoardMember,
  getCeo,
  getChair,
  getDeputy,
  owners,
} from '../../state/editor/selectors';
import { nameFallback } from '../../utils';
import { msgsToComponents } from '../../utils/msgs-to-components';

const PostalPlace = lazyWithPreload(() => import('../PostalPlace'));

const msgs = {
  NameLabel: 'Fullt navn',
  CeoLabel: 'Daglig leder',
  OwnerLabel: {
    one: 'Eier',
    many: 'Eiere',
  },
  PurposeLabel: 'Formål',
  BoardLabel: 'Styre',
  AdressLabel: 'Adresse',
  ShareCapial: 'Aksjekapital',
};

const Msg = msgsToComponents(msgs);

export interface Props {
  info: CoreInfo;
  founder: Person;
  ceo: Person;
  chair: Person | undefined;
  boardMembers: Person[];
  deputies: Person[];
  owners: Owner[];
}

const SummaryBoxContainer = styled.div``;

const SummaryBoxList = styled.dl`
  margin: 0;

  dt:not(:first-of-type) {
    margin-top: 16px;
  }

  dt {
    font-weight: 500;
  }

  dd {
    margin: 0;
  }
`;

function selector(state: RootState) {
  const founder = state.editor.people[state.editor.formOwner];
  return {
    info: state.editor.coreInfo,
    chair: getChair(state),
    boardMembers: getBoardMember(state),
    deputies: getDeputy(state),
    owners: owners(state.editor),
    ceo: getCeo(state),
    founder,
  };
}

export const SummaryBox: React.FC = () => {
  const { boardMembers, ceo, chair, deputies, info, owners } =
    useSelector(selector);
  const { companyCheckThunks } = useActions();

  React.useEffect(() => {
    companyCheckThunks.validateCompanyName(info.name);
  }, [companyCheckThunks, info.name]);

  return (
    <SummaryBoxContainer>
      <SummaryBoxList data-testid="SummaryBoxList">
        <dt>Navn</dt>
        <dd>{info.name}</dd>

        <dt>
          <Msg.OwnerLabel kind={owners.length === 1 ? 'one' : 'many'} />
        </dt>
        <dd>
          {owners.map(owner => {
            return (
              <div key={owner.item.id}>
                {nameFallback(owner.item.name)} (
                {formatters.formatNumber(owner.equity)} aksjer)
              </div>
            );
          })}
        </dd>

        <dt>
          <Msg.ShareCapial />
        </dt>
        <dd>
          {formatters.formatAmount(Number(info.capital || 0), {
            currency: 'kroner',
          })}
        </dd>

        <dt>
          <Msg.BoardLabel />
        </dt>
        <dd>
          <div>{chair.name} (styreleder)</div>
          {boardMembers.map(member => (
            <div key={member.id}>{member.name} (styremedlem)</div>
          ))}
          {deputies.map(member => (
            <div key={member.id}>{member.name} (varamedlem)</div>
          ))}
        </dd>

        {ceo && (
          <>
            <dt>
              <Msg.CeoLabel />
            </dt>
            <dd>{ceo.name}</dd>
          </>
        )}

        <dt>
          <Msg.AdressLabel />
        </dt>
        <dd>
          {info.address}, {info.postalCode}
          &nbsp;
          <React.Suspense fallback={null}>
            <PostalPlace postalCode={info.postalCode} />
          </React.Suspense>
        </dd>

        <dt>
          <Msg.PurposeLabel />
        </dt>
        <dd data-testid="purpose">{info.verbosePurpose}</dd>
      </SummaryBoxList>
    </SummaryBoxContainer>
  );
};
